import { useContext, useEffect, useRef, useState } from "react";
import type { ManagementUpdateCup } from "../../../models/cupsCifSelector/cupsCifSelector.model";
import {
  adaptCupsToContacts,
  EMAIL_REGEX,
  PHONE_REGEX,
} from "../../../../utils/components/managementsUpdate/managementsUpdateContact/managementsUpdateContact.util";
import type {
  ContactState,
  GetContact,
  GetContactDataRequestBody,
  GetContactDataResponseBody,
  HandleState,
} from "../../../models/managementsUpdate/managementsUpdateContact/managementsUpdateContact.model";
import TotalEnergiesContext from "../../../store/context/TotalEnergiesContext";
import { getContactData } from "../../../services/managementsUpdate/managementsUpdate.service";
import { isEmpty, isNull } from "lodash";

const useUpdateContact = (cupsSelected: ManagementUpdateCup[]) => {
  const { ownerData, userData } = useContext(TotalEnergiesContext);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const abortControllerRef = useRef<AbortController>(new AbortController());
  const [contacts, setContacts] = useState<ContactState[]>(
    adaptCupsToContacts(cupsSelected),
  );

  const handleUpdateContact: HandleState = (e, cupIndex, type, typeIndex) => {
    const newContacts = structuredClone(contacts);
    const { value } = e.target;
    newContacts[cupIndex][type][typeIndex].value = value.replace(/\s+/g, "");
    newContacts[cupIndex][type][typeIndex].error = null;
    newContacts[cupIndex].isUpdateContact = true;
    setContacts(newContacts);
  };

  const handleValidateInput: HandleState = (e, cupIndex, type, typeIndex) => {
    const newContacts = structuredClone(contacts);
    const { value } = e.target;
    const contact = newContacts[cupIndex][type][typeIndex];

    console.log({
      newContacts,
      contact,
    });

    const isValid =
      type === "email" ? EMAIL_REGEX.test(value) : PHONE_REGEX.test(value);
    if (contact.required) {
      contact.error = isValid ? null : "required";
    } else if (value) {
      contact.error = isValid ? null : "format";
    }

    if (type === "phone" && isNull(contact.error)) {
      const contactPhoneValues = newContacts[cupIndex].phone
        .map((p: ContactState["phone"][number]) => p.value)
        .filter((v: string) => !isEmpty(v));
      const isPhoneRepetition =
        contactPhoneValues.length !== new Set(contactPhoneValues).size;
      if (isPhoneRepetition) {
        contact.error = "repetition";
      }
    }

    setContacts(newContacts);
  };

  const updateContactState = (
    contractId: number,
    data: GetContactDataResponseBody,
  ) => {
    setContacts((prevContacts) => {
      const updatedContacts = prevContacts.map((contact) => {
        if (contact.cupsContractId === contractId) {
          return {
            ...contact,
            email: Array.from({ length: 5 }, (_, i) => ({
              name: `email${i + 1}`,
              value: data.Emails[i] || "",
              error: null,
              required: i === 0,
            })),
            phone: Array.from({ length: 5 }, (_, i) => ({
              name: `phone${i + 1}`,
              value: data.Telefonos[i] || "",
              error: null,
              required: i === 0,
            })),
            isDataLoaded: true,
          };
        }
        return contact;
      });
      return updatedContacts;
    });
  };

  const getContact: GetContact = (contractId) => {
    if (ownerData && userData) {
      const contact = contacts.find((cup) => cup.cupsContractId === contractId);

      if (contact?.isDataLoaded) {
        return;
      }

      const getContactDataRequestBody: GetContactDataRequestBody = {
        IdContrato: contractId,
        IsAdmin: ownerData.isAdministrator,
      };

      getContactData(
        getContactDataRequestBody,
        userData.token,
        abortControllerRef.current,
      )
        .then((data) => updateContactState(contractId, data))
        .catch(console.log);
    }
  };

  useEffect(() => {
    const abortController = abortControllerRef.current;
    const firstContractId = cupsSelected[0]?.cupsContractId;
    if (firstContractId) getContact(Number(firstContractId));
    return () => {
      abortController.abort();
    };
  }, [cupsSelected]);

  const allValid = contacts.every(
    (cup) =>
      !cup.isDataLoaded ||
      (cup.email.every(
        (email) =>
          (email.required && email.value && EMAIL_REGEX.test(email.value)) ||
          (!email.required &&
            (!email.value || (email.value && EMAIL_REGEX.test(email.value)))),
      ) &&
        cup.phone.every(
          (phone) =>
            isNull(phone.error) &&
            ((phone.required && phone.value && PHONE_REGEX.test(phone.value)) ||
              (!phone.required &&
                (!phone.value ||
                  (phone.value && PHONE_REGEX.test(phone.value))))),
        )),
  );

  useEffect(() => {
    const hasUpdatedContact = contacts.some(
      ({ isUpdateContact }) => isUpdateContact,
    );
    setIsSubmitDisabled(!(allValid && hasUpdatedContact));
  }, [contacts]);

  return {
    contacts,
    handleUpdateContact,
    handleValidateInput,
    isSubmitDisabled,
    getContact,
  };
};

export default useUpdateContact;
